import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { api } from '../../../lib/api';

type ContactUser = {
  id: number;
  full_name: string;
  email: string;
  role: string;
  school_id: number | null;
};

type ContactSubmissionListItem = {
  id: number;
  category: string;
  subject: string;
  contact_email: string | null;
  created_at: string;
  is_read: boolean;
  user: ContactUser | null;
};

type Attachment = {
  original_name: string;
  stored_name: string;
  content_type: string | null;
  size: number;
};

type ContactSubmissionDetail = {
  id: number;
  category: string;
  subject: string;
  message: string;
  contact_email: string | null;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
  ip: string | null;
  user_agent: string | null;
  attachments: Attachment[];
  user: ContactUser | null;
};

type PagedResponse<T> = { items: T[]; total: number; page: number; page_size: number };

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO');
  } catch {
    return String(iso);
  }
}

function fmtBytes(n: number) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let u = 0;
  let x = v;
  while (x >= 1024 && u < units.length - 1) {
    x /= 1024;
    u += 1;
  }
  return `${x.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] bg-card border border-border rounded-xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Inchide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function ContactSubmissionsPanel() {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [data, setData] = useState<PagedResponse<ContactSubmissionListItem> | null>(null);
  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [pageSize, total]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ContactSubmissionDetail | null>(null);

  const load = useCallback(
    async (opts?: { page?: number }) => {
      const nextPage = Math.max(1, Math.min(totalPages, opts?.page ?? page));
      setLoading(true);
      try {
        const resp = await api.get('/api/admin/platform/contact-submissions', {
          params: {
            q: q.trim() || undefined,
            unread_only: unreadOnly ? true : undefined,
            page: nextPage,
            page_size: pageSize,
          },
        });
        setData(resp.data as any);
        setPage(nextPage);
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut incarca mesajele din Contact.');
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, q, totalPages, unreadOnly]
  );

  useEffect(() => {
    void load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = useCallback(async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const resp = await api.get(`/api/admin/platform/contact-submissions/${id}`);
      setDetail(resp.data as any);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut incarca detaliile mesajului.');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const markRead = useCallback(
    async (id: number, isRead: boolean) => {
      try {
        await api.post(`/api/admin/platform/contact-submissions/${id}/mark-read`, { is_read: isRead });
        if (detail?.id === id) setDetail((p) => (p ? { ...p, is_read: isRead, read_at: isRead ? new Date().toISOString() : null } : p));
        void load();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut actualiza statusul.');
      }
    },
    [detail?.id, load]
  );

  const downloadAttachment = useCallback(async (submissionId: number, a: Attachment) => {
    try {
      const resp = await api.get(
        `/api/admin/platform/contact-submissions/${submissionId}/attachments/${encodeURIComponent(a.stored_name)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: a.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.original_name || a.stored_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut descarca fisierul.');
    }
  }, []);

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-foreground">Mesaje Contact</div>
          <div className="text-sm text-muted-foreground">Cereri trimise prin formularul Contact (platforma).</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" iconName="RefreshCw" onClick={() => void load()} disabled={loading}>
            Reincarca
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <Input
          label="Cauta"
          placeholder="Subiect / mesaj / email..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void load({ page: 1 });
          }}
        />
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          <span className="text-muted-foreground">Doar necitite</span>
        </label>
        <div className="flex items-end">
          <Button variant="outline" iconName="Search" onClick={() => void load({ page: 1 })} disabled={loading}>
            Aplica
          </Button>
        </div>
      </div>

      <div className="mt-4 border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-2 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <div className="col-span-2">Data</div>
          <div className="col-span-2">Din</div>
          <div className="col-span-2">Tip</div>
          <div className="col-span-5">Subiect</div>
          <div className="col-span-1 text-right">Status</div>
        </div>

        {items.length === 0 ? (
          <div className="px-3 py-6 text-sm text-muted-foreground">Nu exista mesaje.</div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((s) => {
              const from = s.user?.email || s.contact_email || '—';
              const status = s.is_read ? 'CITIT' : 'NOU';
              return (
                <button
                  key={s.id}
                  className="w-full text-left grid grid-cols-12 gap-2 px-3 py-3 hover:bg-muted/30"
                  onClick={() => void openDetails(s.id)}
                >
                  <div className="col-span-2 text-sm text-foreground">{fmtDateTime(s.created_at)}</div>
                  <div className="col-span-2 text-sm text-foreground truncate">{from}</div>
                  <div className="col-span-2 text-sm text-foreground capitalize">{s.category || '—'}</div>
                  <div className="col-span-5 text-sm text-foreground truncate">{s.subject || '—'}</div>
                  <div className="col-span-1 text-xs text-right">
                    <span
                      className={`inline-flex px-2 py-1 rounded-md border ${
                        s.is_read ? 'border-border text-muted-foreground' : 'border-primary text-primary'
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
        <div className="text-muted-foreground">
          Total: <span className="text-foreground">{total}</span> • Pagina{' '}
          <span className="text-foreground">
            {page}/{totalPages}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" iconName="ChevronLeft" onClick={() => void load({ page: page - 1 })} disabled={loading || page <= 1}>
            Inapoi
          </Button>
          <Button
            variant="outline"
            iconName="ChevronRight"
            onClick={() => void load({ page: page + 1 })}
            disabled={loading || page >= totalPages}
          >
            Inainte
          </Button>
        </div>
      </div>

      {detailOpen ? (
        <Modal
          title={detail ? `#${detail.id} • ${detail.subject || 'Mesaj'}` : 'Mesaj'}
          onClose={() => {
            setDetailOpen(false);
            setDetail(null);
          }}
        >
          {detailLoading ? (
            <div className="py-8 text-sm text-muted-foreground">Se incarca...</div>
          ) : !detail ? (
            <div className="py-8 text-sm text-muted-foreground">Nu am putut incarca mesajul.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-border rounded-lg p-3 bg-background">
                  <div className="text-xs text-muted-foreground">Data</div>
                  <div className="text-sm text-foreground mt-1">{fmtDateTime(detail.created_at)}</div>
                </div>
                <div className="border border-border rounded-lg p-3 bg-background">
                  <div className="text-xs text-muted-foreground">Din</div>
                  <div className="text-sm text-foreground mt-1">
                    {detail.user?.email || detail.contact_email || '—'}
                  </div>
                </div>
                <div className="border border-border rounded-lg p-3 bg-background">
                  <div className="text-xs text-muted-foreground">Status</div>
                  <div className="text-sm text-foreground mt-1">{detail.is_read ? 'CITIT' : 'NOU'}</div>
                </div>
              </div>

              {detail.user ? (
                <div className="text-sm text-muted-foreground">
                  Utilizator: <span className="text-foreground">{detail.user.full_name || '—'}</span> •{' '}
                  <span className="text-foreground">{detail.user.role || '—'}</span> • scoala_id:{' '}
                  <span className="text-foreground">{detail.user.school_id ?? '—'}</span>
                </div>
              ) : null}

              <div className="border border-border rounded-lg p-3 bg-background">
                <div className="text-xs text-muted-foreground mb-2">Mesaj</div>
                <div className="text-sm text-foreground whitespace-pre-wrap">{detail.message || '—'}</div>
              </div>

              {detail.attachments?.length ? (
                <div className="border border-border rounded-lg p-3 bg-background">
                  <div className="text-xs text-muted-foreground mb-2">Atasamente</div>
                  <div className="space-y-2">
                    {detail.attachments.map((a) => (
                      <div key={a.stored_name} className="flex items-center justify-between gap-3">
                        <div className="text-sm text-foreground truncate">
                          {a.original_name}{' '}
                          <span className="text-xs text-muted-foreground">({fmtBytes(a.size)})</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Download"
                          onClick={() => void downloadAttachment(detail.id, a)}
                        >
                          Descarca
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {detail.ip ? `IP: ${detail.ip}` : null}
                  {detail.user_agent ? ` • UA: ${detail.user_agent}` : null}
                </div>
                <div className="flex items-center gap-2">
                  {detail.is_read ? (
                    <Button variant="outline" iconName="Undo2" onClick={() => void markRead(detail.id, false)}>
                      Marcheaza necitit
                    </Button>
                  ) : (
                    <Button variant="default" iconName="Check" onClick={() => void markRead(detail.id, true)}>
                      Marcheaza citit
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
